'use client';

import { useEffect, useState, useRef } from 'react';
import { Bell, X, CheckCircle2, Trash2, Info, MessageSquare } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { API_BASE_URL, apiService } from '@/lib/api';

export default function NotificationSystem({ className, buttonClassName, iconClassName }) {
    const router = useRouter();
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [toast, setToast] = useState(null);
    const eventSourceRef = useRef(null);
    const audioRef = useRef(null);

    const fetchNotifications = async () => {
        try {
            const data = await apiService.getNotifications();
            if (Array.isArray(data)) {
                setNotifications(data);
                const unread = data.filter((n) => !n.read).length;
                setUnreadCount(unread);
            }
        } catch (error) {
            console.warn('⚠️ [NOTIF] Error al obtener notificaciones iniciales:', error?.message || error);
        }
    };

    useEffect(() => {
        fetchNotifications();
        audioRef.current = new Audio('/notification-sound.mp3');
        audioRef.current.volume = 0.5;

        let reconnectTimeout;
        let retryCount = 0;

        const connect = () => {
            const token = localStorage.getItem('token');
            if (!token) return;

            // IMPORTANTE: Asegurar que el stream use la ruta correcta /notif_list/stream
            const urlWithToken = `${API_BASE_URL}/notif_list/stream?token=${token}`;
            const eventSource = new EventSource(urlWithToken);

            eventSource.onopen = () => {
                console.log('✅ SSE conectado correctamente');
                retryCount = 0;
            };

            eventSource.onmessage = (event) => {
                try {
                    if (event.data === ': heartbeat') return;
                    
                    const notification = JSON.parse(event.data);
                    
                    // Si el backend envía un error de token, detener reconexión
                    if (notification?.error) {
                        if (notification.error === 'invalid_token' || notification.error === 'token_required') {
                            console.warn('⚠️ [SSE] Token inválido o expirado. Cerrando stream.');
                            eventSource.close();
                            if (reconnectTimeout) clearTimeout(reconnectTimeout);
                            if (typeof window !== 'undefined') {
                                localStorage.removeItem('token');
                                window.location.href = '/login';
                            }
                        }
                        return;
                    }

                    // Ignorar mensajes sin contenido o sin título (evita notificaciones 'undefined')
                    if (!notification || !notification.title) return;

                    // Disparar evento global para que cualquier componente pueda reaccionar (ej: refrescar lista de propiedades)
                    if (notification && notification.type) {
                        console.log(`📡 [SSE] Despachando evento global: ${notification.type}`);
                        window.dispatchEvent(new CustomEvent(notification.type, { detail: notification }));
                    }
                    
                    // Si la notificación no tiene id (ej: broadcast SSE como property_created),
                    // generamos una clave sintética para evitar el warning de React "key prop"
                    if (!notification.id) {
                        notification._key = `${notification.type || 'notif'}-${Date.now()}`;
                    }

                    // Actualizar lista
                    setNotifications(prev => {
                        const uniqueKey = notification.id || notification._key;
                        if (prev.some(n => (n.id || n._key) === uniqueKey)) return prev;
                        return [notification, ...prev].slice(0, 50);
                    });
                    
                    if (!notification.read) {
                        setUnreadCount(prev => prev + 1);
                    }

                    // Mostrar Toast
                    setToast(notification);
                    setTimeout(() => setToast(null), 8000); // 8 segundos

                    // Sonido
                    if (audioRef.current) {
                        audioRef.current.play().catch(() => { });
                    }

                    // Notificación de navegador (solo si tiene título válido)
                    if (typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'granted' && notification.title) {
                        new window.Notification(notification.title, {
                            body: notification.message || '',
                            icon: '/logo.png',
                            tag: notification.id ? String(notification.id) : undefined
                        });
                    }
                } catch (e) {
                    if (event.data !== ': heartbeat') {
                        console.error("Error parseando notificación SSE:", e);
                    }
                }
            };

            eventSource.onerror = (error) => {
                eventSource.close();
                const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
                if (!token) {
                    console.warn('⚠️ SSE sin token disponible. Deteniendo reintentos.');
                    return;
                }
                const nextRetry = Math.min(1000 * Math.pow(2, retryCount), 30000);
                retryCount++;
                console.warn(`⚠️ SSE desconectado. Reintentando en ${nextRetry / 1000}s...`);
                reconnectTimeout = setTimeout(connect, nextRetry);
            };

            eventSourceRef.current = eventSource;
        };

        connect();

        if (typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'default') {
            window.Notification.requestPermission();
        }

        const handleNotificationClick = (notification) => {
        markAsRead(notification.id || notification._key);
        if (notification.type === 'whatsapp') {
            router.push('/dashboard/settings?tab=whatsapp');
            setIsOpen(false);
        }
    };

    return () => {
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
            if (eventSourceRef.current) eventSourceRef.current.close();
        };
    }, []);

    const markAsRead = async (id, e) => {
        if (e) e.stopPropagation();
        if (!id) return;

        // Desaparece inmediatamente de la lista activa al marcarse como leída
        setNotifications(prev => prev.filter(n => n.id !== id && n._key !== id));
        setUnreadCount(prev => Math.max(0, prev - 1));

        // Sincronizar lectura con backend si es ID numérico de BD
        const numericId = Number(id);
        const isNumericDbId = Number.isInteger(numericId) && numericId > 0 && String(id).indexOf('notif-') === -1;

        if (isNumericDbId) {
            try {
                await apiService.markNotificationRead(numericId);
            } catch (error) {
                console.warn('⚠️ [NOTIF] No se pudo sincronizar lectura en backend:', error?.message || error);
            }
        }
    };

    const deleteNotification = async (id, e) => {
        if (e) e.stopPropagation();
        if (!id) return;

        const target = notifications.find(n => (n.id === id) || (n._key && n._key === id));
        const wasUnread = target && !target.read;

        // Desaparece inmediatamente de la lista
        setNotifications(prev => prev.filter(n => n.id !== id && n._key !== id));
        if (wasUnread) {
            setUnreadCount(prev => Math.max(0, prev - 1));
        }

        const numericId = Number(id);
        const isNumericDbId = Number.isInteger(numericId) && numericId > 0 && String(id).indexOf('notif-') === -1;

        if (isNumericDbId) {
            try {
                await apiService.deleteNotification(numericId);
            } catch (error) {
                console.warn('⚠️ [NOTIF] Error eliminando notificación en backend:', error?.message || error);
            }
        }
    };

    const markAllAsRead = async () => {
        if (notifications.length === 0) return;
        
        // Desaparecen inmediatamente todas las notificaciones
        setNotifications([]);
        setUnreadCount(0);

        try {
            await apiService.markAllNotificationsRead();
        } catch (error) {
            console.warn('⚠️ [NOTIF] Error marcando todas como leídas:', error?.message || error);
        }
    };

    const clearAll = async () => {
        if (notifications.length === 0) return;
        
        const confirmResult = window.confirm('¿Estás seguro de que deseas eliminar todas las notificaciones?');
        if (!confirmResult) return;

        setNotifications([]);
        setUnreadCount(0);

        try {
            await apiService.clearNotifications();
        } catch (error) {
            console.warn('⚠️ [NOTIF] Error limpiando notificaciones:', error?.message || error);
        }
    };
    const handleNotificationClick = (notification) => {
        markAsRead(notification.id || notification._key);
        if (notification.type === 'whatsapp') {
            router.push('/dashboard/settings?tab=whatsapp');
            setIsOpen(false);
        }
    };

    return (
        <>
            <div className={`relative ${className || ''}`}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={buttonClassName || "relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"}
                >
                    <Bell className={iconClassName || "w-6 h-6"} />
                    {unreadCount > 0 && (
                        <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </button>

                {isOpen && (
                    <>
                        <div
                            className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px] sm:bg-transparent sm:backdrop-blur-none"
                            onClick={() => setIsOpen(false)}
                        />

                        <div className="fixed inset-x-2.5 top-[60px] sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 w-auto sm:w-96 max-w-none sm:max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[calc(100dvh-5rem)] sm:max-h-[600px] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                            <div className="flex items-center justify-between p-3.5 sm:p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
                                <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                        <Bell className="w-4 h-4" />
                                    </div>
                                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Notificaciones</h3>
                                    {unreadCount > 0 && (
                                        <span className="px-2 py-0.5 text-xs font-semibold text-white bg-blue-600 rounded-full">
                                            {unreadCount}
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                                    aria-label="Cerrar notificaciones"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {notifications.length > 0 && (
                                <div className="flex gap-2 p-2 border-b border-slate-200 bg-slate-50 shrink-0">
                                    <button
                                        type="button"
                                        onClick={markAllAsRead}
                                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 hover:text-slate-900 rounded-lg shadow-2xs transition-colors cursor-pointer"
                                    >
                                        <CheckCircle2 size={14} className="text-emerald-600" />
                                        <span>Marcar leídas</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={clearAll}
                                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-700 bg-rose-50/70 border border-rose-200 hover:bg-rose-100 rounded-lg shadow-2xs transition-colors cursor-pointer"
                                    >
                                        <Trash2 size={14} className="text-rose-600" />
                                        <span>Limpiar todo</span>
                                    </button>
                                </div>
                            )}

                            <div className="overflow-y-auto flex-1">
                                {notifications.length === 0 ? (
                                    <div className="p-8 text-center text-gray-500">
                                        <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                        <p className="text-sm">No hay notificaciones</p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-100">
                                        {notifications.map((notification, index) => (
                                            <div
                                                key={notification.id ?? notification._key ?? index}
                                                onClick={() => handleNotificationClick(notification)}
                                                className={`p-3.5 sm:p-4 hover:bg-gray-100/80 cursor-pointer transition-colors ${!notification.read ? 'bg-blue-50/60' : ''
                                                    }`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className="flex-shrink-0 pt-0.5">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                            notification.type === 'whatsapp' ? 'bg-rose-100 text-rose-600 ring-2 ring-rose-200' :
                                                            notification.type === 'calendar' ? 'bg-green-100 text-green-600' : 
                                                            notification.type === 'lead' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                            {notification.type === 'whatsapp' ? <MessageSquare size={16} /> : notification.type === 'calendar' ? <CheckCircle2 size={16} /> : <Bell size={16} />}
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex justify-between items-start gap-2">
                                                            <p className="text-sm font-semibold text-gray-900 break-words leading-tight pr-1">
                                                                {notification.title}
                                                            </p>
                                                            <div className="flex items-center gap-1.5 shrink-0">
                                                                {!notification.read && (
                                                                    <span className="w-2 h-2 bg-blue-600 rounded-full" title="No leída"></span>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => deleteNotification(notification.id || notification._key, e)}
                                                                    className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer"
                                                                    title="Eliminar notificación"
                                                                >
                                                                    <Trash2 size={13} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words leading-relaxed whitespace-pre-line">
                                                            {notification.message}
                                                        </p>
                                                        <div className="flex items-center justify-between mt-1.5 pt-1 border-t border-gray-100/80">
                                                            <span className="text-[10px] text-gray-400 uppercase font-medium">
                                                                {(() => {
                                                                    try {
                                                                        const d = notification.created_at ? new Date(notification.created_at) : new Date();
                                                                        return isNaN(d.getTime())
                                                                            ? new Date().toLocaleString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                                                                            : d.toLocaleString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
                                                                    } catch {
                                                                        return '';
                                                                    }
                                                                })()}
                                                            </span>
                                                            {!notification.read && (
                                                                <span
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        markAsRead(notification.id || notification._key);
                                                                    }}
                                                                    className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 cursor-pointer"
                                                                >
                                                                    Tocar para marcar leída
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Pop-up (Toast) responsivo para móvil y escritorio */}
            {toast && (
                <div 
                    onClick={() => {
                        if (toast.type === 'whatsapp') {
                            router.push('/dashboard/settings?tab=whatsapp');
                            setToast(null);
                        }
                    }}
                    className={`fixed top-16 left-3 right-3 sm:left-auto sm:right-5 sm:w-80 bg-white rounded-xl shadow-2xl border-l-[6px] ${
                        toast.type === 'whatsapp' ? 'border-rose-500 cursor-pointer ring-1 ring-rose-200' : 'border-blue-600'
                    } p-4 flex gap-3 items-start pointer-events-auto z-[999999] animate-in fade-in slide-in-from-top-2 duration-200`}
                >
                    <div className={`${toast.type === 'whatsapp' ? 'bg-rose-50 text-rose-600' : 'bg-blue-50 text-blue-600'} p-2 rounded-lg shrink-0`}>
                        {toast.type === 'whatsapp' ? <MessageSquare size={20} /> : <Bell size={20} />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="m-0 font-bold text-gray-900 text-sm break-words">{toast.title}</h4>
                        <p className="m-0 mt-1 text-xs text-gray-600 leading-snug break-words whitespace-pre-line">{toast.message}</p>
                    </div>
                    <button 
                        onClick={() => setToast(null)}
                        className="text-gray-400 hover:text-gray-600 p-1 shrink-0 -mr-1 -mt-1 cursor-pointer"
                        aria-label="Cerrar notificación"
                    >
                        <X size={16} />
                    </button>
                    <div className="absolute top-0 left-0 w-full h-[3px] bg-gray-100 overflow-hidden rounded-t-xl">
                        <div className="animate-progress h-full bg-blue-600"></div>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes progress {
                    0% { transform: scaleX(1); }
                    100% { transform: scaleX(0); }
                }
                .animate-progress {
                    animation: progress 8s linear forwards;
                }
            `}</style>
        </>
    );
}
